import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PendingApplications = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);

  const applications = [
    {
      id: 1,
      vendorName: 'PureSkin Cosmetics',
      businessName: 'PureSkin Beauty LLC',
      email: 'contact@pureskin.com',
      phone: '+1 (555) 123-4567',
      category: 'Skincare',
      submittedDate: '2025-11-15',
      status: 'pending',
      documents: {
        businessLicense: true,
        taxId: true,
        productCatalog: true,
        qualityCerts: false
      },
      description: 'Premium organic skincare line focused on natural ingredients and sustainable practices.',
      requestedCategories: ['Facial Care', 'Body Care', 'Anti-Aging'],
      estimatedMonthlyVolume: '$25,000 - $50,000',
      companyAge: '3 years',
      website: 'https://pureskin.com'
    },
    {
      id: 2,
      vendorName: 'Luxe Hair Studio',
      businessName: 'Luxe Professional Hair Products Inc.',
      email: 'vendor@luxehair.com',
      phone: '+1 (555) 234-5678',
      category: 'Hair Care',
      submittedDate: '2025-11-16',
      status: 'pending',
      documents: {
        businessLicense: true,
        taxId: true,
        productCatalog: true,
        qualityCerts: true
      },
      description: 'Professional-grade hair care products for salons and retail customers.',
      requestedCategories: ['Shampoo & Conditioner', 'Styling Products', 'Hair Treatments'],
      estimatedMonthlyVolume: '$50,000 - $100,000',
      companyAge: '7 years',
      website: 'https://luxehair.com'
    },
    {
      id: 3,
      vendorName: 'GlowUp Cosmetics',
      businessName: 'GlowUp Beauty International',
      email: 'partners@glowup.com',
      phone: '+1 (555) 345-6789',
      category: 'Makeup',
      submittedDate: '2025-11-17',
      status: 'under_review',
      documents: {
        businessLicense: true,
        taxId: true,
        productCatalog: true,
        qualityCerts: true
      },
      description: 'Trend-focused makeup brand offering innovative color cosmetics and beauty tools.',
      requestedCategories: ['Face Makeup', 'Eye Makeup', 'Lip Products'],
      estimatedMonthlyVolume: '$75,000 - $150,000',
      companyAge: '5 years',
      website: 'https://glowup.com'
    }
  ];

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending Review', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'Clock' },
      under_review: { label: 'Under Review', color: 'text-info', bgColor: 'bg-info/10', icon: 'Eye' },
      approved: { label: 'Approved', color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle' },
      rejected: { label: 'Rejected', color: 'text-error', bgColor: 'bg-error/10', icon: 'XCircle' }
    };
    return configs?.[status] || configs?.pending;
  };

  const handleApprove = (application) => {
    console.log('Approving vendor:', application?.vendorName);
    // Approval logic here
  };

  const handleReject = (application) => {
    console.log('Rejecting vendor:', application?.vendorName);
    // Rejection logic here
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
  };

  const filteredApplications = applications?.filter(app => {
    const matchesSearch = app?.vendorName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         app?.businessName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         app?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || app?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by vendor name, business, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              leftIcon="Search"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'under_review']?.map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {status === 'all' ? 'All' : status?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {/* Applications List */}
      <div className="space-y-4">
        {filteredApplications?.map((application) => {
          const statusConfig = getStatusConfig(application?.status);
          const docsComplete = Object.values(application?.documents || {})?.every(doc => doc === true);
          
          return (
            <div
              key={application?.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-elevation-2 transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                {/* Vendor Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-foreground">
                        {application?.vendorName}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {application?.businessName}
                      </p>
                    </div>
                    
                    <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 ${statusConfig?.bgColor}`}>
                      <Icon name={statusConfig?.icon} size={14} className={statusConfig?.color} />
                      <span className={`text-xs font-medium ${statusConfig?.color}`}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Mail" size={16} />
                      <span>{application?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Phone" size={16} />
                      <span>{application?.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Tag" size={16} />
                      <span>{application?.category}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>Applied {new Date(application?.submittedDate)?.toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Document Status */}
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-foreground">Documents:</span>
                    <div className="flex items-center gap-2">
                      <Icon 
                        name={docsComplete ? 'CheckCircle2' : 'AlertCircle'} 
                        size={16} 
                        className={docsComplete ? 'text-success' : 'text-warning'} 
                      />
                      <span className={`text-sm ${docsComplete ? 'text-success' : 'text-warning'}`}>
                        {Object.values(application?.documents || {})?.filter(Boolean)?.length} / {Object.keys(application?.documents || {})?.length} Complete
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 lg:w-48">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="CheckCircle"
                    iconPosition="left"
                    onClick={() => handleApprove(application)}
                    disabled={!docsComplete}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => handleViewDetails(application)}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="destructive"
                    fullWidth
                    iconName="XCircle"
                    iconPosition="left"
                    onClick={() => handleReject(application)}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filteredApplications?.length === 0 && (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No applications found
          </h3>
          <p className="text-muted-foreground">
            {searchQuery ? 'Try adjusting your search criteria' : 'No pending vendor applications at this time'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PendingApplications;